import type { ReactNode } from 'react';
import type { FormikProps } from 'formik';
import { Input } from '../../../components/ui';
import { UploadIcon } from '../../../assets/icons';
import type { OrgFormValues } from './types';

const TEXTAREA =
  'w-full resize-none rounded-lg border border-line bg-white px-3.5 py-3 text-sm text-ink placeholder:text-ink-secondary/60 transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

function Card({
  title,
  children,
  bodyClassName = 'p-6',
}: {
  title?: string;
  children: ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-line bg-white shadow-card">
      {title && (
        <div className="border-b border-line px-6 py-4">
          <h2 className="text-lg font-medium text-ink-900">{title}</h2>
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

function Field({
  label,
  htmlFor,
  required,
  error,
  className = '',
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={['flex flex-col gap-1.5', className].join(' ')}>
      <label htmlFor={htmlFor} className="text-xs font-medium tracking-[0.6px] text-ink-700">
        {label}
        {required && <span className="text-[#ba1a1a]"> *</span>}
      </label>
      {children}
      {error && <p className="text-xs font-medium text-[#ba1a1a]">{error}</p>}
    </div>
  );
}

/** Step 1 — Organization Identity, Location & Contact, Status, Logo. */
export default function OrganizationDetailsStep({ formik }: { formik: FormikProps<OrgFormValues> }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formik;

  // Only show a field's error once it has been touched (or after a submit attempt).
  const errorOf = (name: keyof OrgFormValues) =>
    touched[name] && errors[name] ? (errors[name] as string) : undefined;
  const invalid = (name: keyof OrgFormValues) => (errorOf(name) ? 'error' : '');
  const textareaCls = (name: keyof OrgFormValues) =>
    [TEXTAREA, errorOf(name) ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : ''].join(
      ' ',
    );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      {/* Main column */}
      <div className="flex flex-col gap-6 lg:col-span-8">
        <Card title="Organization Identity">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field
              label="Organization Name"
              htmlFor="name"
              required
              error={errorOf('name')}
              className="sm:col-span-2"
            >
              <Input
                id="name"
                name="name"
                placeholder="e.g. Municipal Governance Department"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                status={invalid('name')}
              />
            </Field>
            <Field label="Short Name" htmlFor="shortName" required error={errorOf('shortName')}>
              <Input
                id="shortName"
                name="shortName"
                placeholder="e.g. MGD"
                value={values.shortName}
                onChange={handleChange}
                onBlur={handleBlur}
                status={invalid('shortName')}
              />
            </Field>
            <Field label="Internal Code" htmlFor="internalCode">
              <Input
                id="internalCode"
                name="internalCode"
                placeholder="Auto-generated on save"
                value={values.internalCode}
                readOnly
                disabled
                title="Generated automatically by the system"
                className="cursor-not-allowed bg-[#f1f5f9] text-ink-secondary"
              />
            </Field>
            <Field
              label="Description"
              htmlFor="description"
              required
              error={errorOf('description')}
              className="sm:col-span-2"
            >
              <textarea
                id="description"
                name="description"
                rows={4}
                placeholder="Brief summary of the organization's primary functions..."
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                className={textareaCls('description')}
              />
            </Field>
          </div>
        </Card>

        <Card title="Location & Contact">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <Field
              label="Address"
              htmlFor="address"
              required
              error={errorOf('address')}
              className="sm:col-span-2"
            >
              <textarea
                id="address"
                name="address"
                rows={3}
                placeholder="Full physical address..."
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                className={textareaCls('address')}
              />
            </Field>
            <Field
              label="Contact Person"
              htmlFor="contactPerson"
              required
              error={errorOf('contactPerson')}
            >
              <Input
                id="contactPerson"
                name="contactPerson"
                placeholder="Full name"
                value={values.contactPerson}
                onChange={handleChange}
                onBlur={handleBlur}
                status={invalid('contactPerson')}
              />
            </Field>
            <Field label="Phone Number" htmlFor="phone" required error={errorOf('phone')}>
              <div
                className={[
                  'flex items-center rounded-lg border bg-white transition-colors focus-within:ring-2',
                  errorOf('phone')
                    ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-100'
                    : 'border-line focus-within:border-brand focus-within:ring-brand/15',
                ].join(' ')}
              >
                <span className="flex shrink-0 items-center border-r border-line px-3 text-sm font-medium text-ink-700">
                  +91
                </span>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="98765 43210"
                  value={values.phone}
                  onChange={(e) =>
                    setFieldValue('phone', e.target.value.replace(/\D/g, '').slice(0, 10))
                  }
                  onBlur={handleBlur}
                  className="h-11 w-full rounded-r-lg bg-transparent px-3.5 text-sm text-ink placeholder:text-ink-secondary/60 focus:outline-none"
                />
              </div>
            </Field>
            <Field
              label="Email Address"
              htmlFor="email"
              required
              error={errorOf('email')}
              className="sm:col-span-2"
            >
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="org@example.gov"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                status={invalid('email')}
              />
            </Field>
          </div>
        </Card>
      </div>

      {/* Sidebar column */}
      <div className="flex flex-col gap-6 lg:col-span-4">
        <Card bodyClassName="p-6">
          <Field label="Status">
            <div className="flex gap-4">
              {(['active', 'inactive'] as const).map((s) => {
                const selected = values.status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFieldValue('status', s)}
                    className={[
                      'flex-1 rounded-lg border p-3 text-xs font-bold capitalize tracking-[0.6px] transition-colors',
                      selected
                        ? 'border-brand bg-brand-shade text-brand'
                        : 'border-line text-ink-700 hover:border-brand/40',
                    ].join(' ')}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </Field>
        </Card>

        <Card title="Organization Logo">
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-line p-8 text-center transition-colors hover:border-brand/50">
            <span className="flex h-[45px] w-16 items-center justify-center rounded-full bg-brand-shade text-brand">
              <UploadIcon className="h-[26.667px] w-[21.333px]" />
            </span>
            <span className="text-xs font-bold tracking-[0.6px] text-ink-900">
              {values.logo ? values.logo.name : 'Click to upload or drag and drop'}
            </span>
            <span className="text-xs font-medium tracking-[0.6px] text-ink-700">
              SVG, PNG, or JPG (max 5MB)
            </span>
            <input
              type="file"
              name="logo"
              accept=".svg,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => setFieldValue('logo', e.currentTarget.files?.[0] ?? null)}
            />
          </label>
        </Card>
      </div>
    </div>
  );
}
